"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { X, MapPin, ShieldCheck, Maximize, Clock, Navigation, ArrowLeft, Calendar, Phone } from "lucide-react";
import { useState, use } from "react";
import { FounderTrustPanel } from "@/components/common/FounderTrustPanel";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { LeadForm } from "@/components/forms/LeadForm";
import { Layout } from "@/components/layout/Layout";
import { getProject, projects } from "@/content/projects";
import { site } from "@/content/site";
import { getProjectJsonLd } from "@/lib/seo";
import Link from "next/link";

interface PreviewImage {
  src: string;
  alt: string;
  label?: string;
}

export default function ProjectDetail({ params }: { params: any }) {
  const resolvedParams = params instanceof Promise ? use(params) : params;
  const slug = (resolvedParams as any).slug;
  const project = getProject(slug);
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null);

  if (!project) {
    return (
      <Layout title="Project Not Found" description="The requested project could not be found.">
        <div className="flex h-[50vh] items-center justify-center">
          <p className="text-xl text-[var(--muted)]">Project not found.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`${project.name} | Swargaseema Project`} description={project.summary} image={project.hero} type="article" jsonLd={getProjectJsonLd(project)} hideFooterForm={true}>
      <main>
        <section className="relative flex min-h-[60vh] sm:min-h-[75vh] flex-col justify-center overflow-hidden bg-slate-900 pb-12 pt-28 sm:pb-16 sm:pt-32 lg:pb-24 lg:pt-48">
          <div className="absolute inset-0 z-0">
            {project.hero && (
              <Image src={project.hero} alt={project.name} fill className="object-cover" priority />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b14]/90 via-[#0d1b14]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b14] via-transparent to-transparent opacity-80" />
          </div>
          
          <Container className="relative z-10 w-full">
            <div className="mb-6 sm:mb-10">
              <Link href="/projects" className="inline-flex items-center text-sm font-bold text-white transition hover:text-[var(--gold)]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to projects
              </Link>
            </div>
            
            <div className="flex flex-col gap-8 sm:gap-12 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                {project.status && (
                  <p className="mb-3 sm:mb-4 text-xs font-black uppercase tracking-[0.15em] text-[var(--gold)]">
                    {project.status}
                  </p>
                )}
                
                <h1 className="font-serif text-3xl font-medium leading-tight text-white sm:text-6xl lg:text-7xl drop-shadow-lg">
                  {project.name}
                </h1>
                
                {project.summary && (
                  <p className="mt-4 sm:mt-6 text-base leading-relaxed text-white/90 sm:text-xl">
                    {project.summary}
                  </p>
                )}
                
                <div className="mt-8 flex flex-wrap items-center gap-6 text-sm font-bold text-white">
                  {project.location && (
                    <div className="flex items-center">
                      <MapPin className="mr-2 h-4 w-4 shrink-0 text-[var(--gold)]" />
                      {project.location}
                    </div>
                  )}
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4 shrink-0 text-[var(--gold)]" />
                    Site visits by appointment
                  </div>
                </div>
              </div>
              
              {project.logo && (
                <div className="relative shrink-0 hidden lg:block">
                  <Image
                    src={project.logo}
                    alt={`${project.name} logo`}
                    width={280}
                    height={100}
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </Container>
        </section>

        <section className="bg-[var(--cream)] py-16 lg:py-24">
          <Container className="grid items-start gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="min-w-0"
            >
              <p className="mb-4 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold-strong)]">Project Overview</p>
              <h2 className="font-serif text-3xl font-medium leading-[1.3] text-[var(--ink)] sm:text-4xl lg:text-5xl">
                {project.summary}
              </h2>
              <p className="mt-8 text-base leading-relaxed text-[var(--muted)] lg:text-lg">
                {project.description}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="sticky top-24"
            >
              {project.approval && (
                <div className="rounded-xl border border-[var(--gold-strong)]/20 bg-white p-8 shadow-[0_12px_40px_rgba(212,175,55,0.08)] relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-6 opacity-[0.03]">
                     <ShieldCheck className="w-48 h-48" />
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 text-[var(--gold-strong)] mb-4">
                      <ShieldCheck className="h-5 w-5 shrink-0" />
                      <span className="text-xs font-black uppercase tracking-[0.15em]">Approval Status</span>
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-[var(--ink)] mb-4">
                      {project.approval}
                    </h3>
                    <p className="text-sm leading-relaxed text-[var(--muted)]">
                      Approval and document references can be shared during enquiry. This project meets all regulatory guidelines for plotted development.
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </Container>
        </section>

        {!!project.layoutImages?.length && (
          <section className="pb-16 lg:pb-24">
            <Container>
              <div className="mx-auto max-w-2xl text-center">
                <SectionHeading eyebrow="Layout context" title="Project layout and planning references." centered />
              </div>
              <div className="mt-10 flex justify-center">
                {project.layoutImages.map((item: any, index: number) => (
                  <motion.div
                    key={item.src}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="w-full max-w-4xl"
                  >
                    <button onClick={() => setPreviewImage(item)} aria-label={`Open ${item.label} for ${project.name}`} className="group w-full rounded-xl border border-[var(--line)] bg-[var(--white)] p-4 shadow-[0_12px_34px_rgba(23,60,49,0.07)] transition hover:shadow-[0_16px_40px_rgba(23,60,49,0.12)] text-left focus:outline-none">
                      <div className="relative aspect-[1.45] overflow-hidden rounded-lg bg-white">
                        <Image src={item.src} alt={item.alt} fill sizes="(max-width: 1200px) 100vw, 1024px" className="object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
                      </div>
                      <div className="mt-4 flex items-center justify-between px-2 pb-1">
                        <span className="text-sm font-black text-[var(--ink)]">{item.label}</span>
                        <span className="text-xs font-bold text-[var(--gold-strong)] uppercase tracking-[0.15em]">Click to enlarge</span>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </div>
            </Container>
          </section>
        )}

        {!!project.featuredImages?.length && (
          <section className="bg-[var(--cream)] py-16 lg:py-24">
            <Container className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {project.featuredImages.map((item: any, index: number) => (
                <motion.article
                  key={item.src}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="flex flex-col overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--white)] shadow-[0_16px_40px_rgba(23,60,49,0.09)]"
                >
                  <div className="relative aspect-video bg-[var(--ink)] shrink-0">
                    <Image src={item.src} alt={item.alt} fill sizes="(max-width: 1024px) 50vw, 33vw" className="object-cover" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-[var(--gold)]">Site proof</p>
                    <h3 className="font-serif text-xl sm:text-2xl font-medium text-[var(--ink)] leading-tight">{item.title}</h3>
                    <p className="mt-2.5 text-sm leading-6 text-[var(--muted)]">{item.copy}</p>
                  </div>
                </motion.article>
              ))}
            </Container>
          </section>
        )}

        {/* <ProjectGallery project={project} /> */}

        <section id="enquiry-form" className="bg-[var(--cream)] py-16 lg:py-24 border-t border-[var(--line)]">
          <Container>
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <SectionHeading
                eyebrow="Private enquiry"
                title={`Book a guided visit for ${project.name}.`}
                copy="Submit your details and the team can confirm availability, documentation, and route guidance."
                centered
              />
            </div>
            <div className="mx-auto grid max-w-5xl items-stretch gap-8 lg:grid-cols-[1.1fr_0.9fr]">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <FounderTrustPanel
                  image="/brand/founder-contact-gesture.webp"
                  title="Get direct guidance before you travel."
                  copy={`Ask the team about ${project.name} availability, documents, location access, and the best time for a site visit.`}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <LeadForm selectedProject={project.name} />
              </motion.div>
            </div>
          </Container>
        </section>
      </main>

      {/* Mobile Sticky Quick CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 flex items-center gap-3 border-t border-black/10 bg-[var(--surface-raised)]/95 p-3 backdrop-blur-xl shadow-2xl lg:hidden pb-safe">
        <a
          href={`tel:${site.phone.replaceAll(" ", "")}`}
          className="flex min-h-[46px] flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-xs font-black uppercase tracking-wider text-white transition active:scale-[0.98]"
        >
          <Phone size={15} className="text-[var(--gold)]" /> Call Us
        </a>
        <a
          href="#enquiry-form"
          className="flex min-h-[46px] flex-[1.4] items-center justify-center gap-2 rounded-xl bg-[var(--gold-strong)] px-4 text-xs font-black uppercase tracking-wider text-[var(--ink)] shadow-md transition active:scale-[0.98]"
        >
          Book Site Visit
        </a>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#10261f]/95 p-4 sm:p-8 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label={previewImage.label}
          onClick={() => setPreviewImage(null)}
        >
          <button
            type="button"
            aria-label="Close image preview"
            onClick={() => setPreviewImage(null)}
            className="absolute right-4 top-4 sm:right-8 sm:top-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition focus:outline-none"
          >
            <X size={24} />
          </button>
          <div className="relative h-full w-full max-w-7xl" onClick={(e) => e.stopPropagation()}>
            <Image src={previewImage.src} alt={previewImage.alt || ""} fill className="object-contain" />
          </div>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm tracking-wide font-black uppercase">
            {previewImage.label}
          </div>
        </div>
      )}
    </Layout>
  );
}
